import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  CircularProgress,
  Typography,
  Dialog,
  IconButton,
  Fade,
  Container,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WestIcon from '@mui/icons-material/West';
import EastIcon from '@mui/icons-material/East';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import CollectionsIcon from '@mui/icons-material/Collections';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { useTranslation } from 'react-i18next';

const importImages = require.context('../assets/gallery', false, /\.(jpg|jpeg|png|gif)$/);

const Gallery = () => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(null);
  const { t } = useTranslation();

  useEffect(() => {
    const imageNames = importImages.keys().map(importImages);
    setPhotos(imageNames);
    setLoading(false);
    AOS.init({ duration: 600, easing: 'ease-out-cubic', once: true });
  }, []);

  const openPhoto = (index) => setSelectedPhotoIndex(index);
  const closePhoto = () => setSelectedPhotoIndex(null);

  const handlePrev = useCallback(() => {
    setSelectedPhotoIndex((i) => Math.max(0, i - 1));
  }, []);

  const handleNext = useCallback(() => {
    setSelectedPhotoIndex((i) => Math.min(photos.length - 1, i + 1));
  }, [photos.length]);

  useEffect(() => {
    if (selectedPhotoIndex === null) return;
    const handleKey = (e) => {
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'ArrowRight') handleNext();
      if (e.key === 'Escape') closePhoto();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [selectedPhotoIndex, handlePrev, handleNext]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', bgcolor: '#ffffff' }}>
        <CircularProgress thickness={1.5} size={48} sx={{ color: '#f97316' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh' }}>

      {/* ── HERO HEADER ── */}
      <Box sx={{
        position: 'relative',
        pt: { xs: 11, md: 15 },
        pb: { xs: 8, md: 10 },
        bgcolor: '#ffffff',
        overflow: 'hidden',
        borderBottom: '1px solid #e2e8f0',
      }}>
        {/* Dot texture */}
        <Box sx={{
          position: 'absolute', inset: 0,
          backgroundImage: 'radial-gradient(rgba(0,0,0,0.045) 1px, transparent 1px)',
          backgroundSize: '24px 24px',
          pointerEvents: 'none',
        }} />

        {/* Orange ambient glow */}
        <Box sx={{
          position: 'absolute', top: '-20%', left: '50%', transform: 'translateX(-50%)',
          width: 700, height: 400,
          background: 'radial-gradient(ellipse, rgba(249,115,22,0.07) 0%, transparent 65%)',
          pointerEvents: 'none',
        }} />

        {/* Right accent line */}
        <Box sx={{
          position: 'absolute', right: '8%', top: '10%',
          width: 1, height: '70%',
          background: 'linear-gradient(to bottom, transparent, rgba(0,0,0,0.07), transparent)',
          display: { xs: 'none', md: 'block' },
        }} />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          {/* Eyebrow */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
            <Box sx={{ width: 28, height: 2, bgcolor: '#f97316', borderRadius: 1 }} />
            <Typography sx={{
              color: '#f97316', fontSize: '0.7rem', fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: 3,
            }}>
              CV. Mulia Utama Mandiri
            </Typography>
          </Box>

          {/* Title row */}
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: { xs: 2, md: 3 }, flexWrap: 'wrap', mb: 3 }}>
            <Typography sx={{
              fontWeight: 900,
              color: '#0f172a',
              fontSize: { xs: '3.5rem', md: '6.5rem' },
              lineHeight: 1,
              letterSpacing: '-0.05em',
              textTransform: 'uppercase',
            }}>
              {t('galeri.header')}
            </Typography>

            {/* Photo count pill */}
            <Box sx={{
              display: 'inline-flex', alignItems: 'center', gap: 1,
              bgcolor: '#fff7ed',
              border: '1px solid #fed7aa',
              px: 2, py: 0.9, borderRadius: '100px',
              mb: { xs: 0.5, md: 1.5 },
            }}>
              <CollectionsIcon sx={{ color: '#f97316', fontSize: '0.85rem' }} />
              <Typography sx={{ color: '#ea580c', fontSize: '0.75rem', fontWeight: 700, letterSpacing: 0.5 }}>
                {photos.length} Foto
              </Typography>
            </Box>
          </Box>

          {/* Subtitle */}
          <Typography sx={{
            color: '#64748b',
            fontSize: { xs: '0.95rem', md: '1.05rem' },
            fontWeight: 400,
            maxWidth: 480,
            lineHeight: 1.75,
          }}>
            {t('galeri.description')}
          </Typography>
        </Container>
      </Box>

      {/* ── GALLERY GRID ── */}
      <Container maxWidth="xl" sx={{ py: { xs: 5, md: 8 }, px: { xs: 2, sm: 3, md: 4 } }}>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' },
          gap: { xs: 2, md: 2.5 },
        }}>
          {photos.map((photo, index) => (
            <Box
              key={index}
              data-aos="fade-up"
              data-aos-delay={(index % 4) * 70}
              onClick={() => openPhoto(index)}
              sx={{
                position: 'relative',
                cursor: 'pointer',
                borderRadius: '16px',
                overflow: 'hidden',
                aspectRatio: '1 / 1',
                bgcolor: '#e2e8f0',
                boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                transition: 'box-shadow 0.3s ease, transform 0.3s ease',
                '&:hover': {
                  boxShadow: '0 12px 32px rgba(0,0,0,0.12)',
                  transform: 'translateY(-4px)',
                },
                '&::after': {
                  content: '""',
                  position: 'absolute', inset: 0,
                  background: 'linear-gradient(to top, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.05) 55%, transparent 100%)',
                  opacity: 0,
                  transition: 'opacity 0.35s ease',
                  zIndex: 1,
                  pointerEvents: 'none',
                },
                '&:hover::after': { opacity: 1 },
                '& img': { transition: 'transform 0.55s cubic-bezier(0.25, 0.46, 0.45, 0.94)' },
                '&:hover img': { transform: 'scale(1.07)' },
                '& .zoom-btn': {
                  opacity: 0,
                  transform: 'scale(0.82)',
                  transition: 'opacity 0.3s ease, transform 0.3s ease',
                },
                '&:hover .zoom-btn': { opacity: 1, transform: 'scale(1)' },
                '& .img-num': {
                  opacity: 0,
                  transform: 'translateY(5px)',
                  transition: 'opacity 0.3s ease, transform 0.3s ease',
                },
                '&:hover .img-num': { opacity: 1, transform: 'translateY(0)' },
              }}
            >
              <img
                src={photo}
                alt={`Gallery ${index + 1}`}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />

              {/* Zoom icon */}
              <Box className="zoom-btn" sx={{
                position: 'absolute', inset: 0, zIndex: 2,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Box sx={{
                  width: { xs: 40, md: 50 }, height: { xs: 40, md: 50 },
                  borderRadius: '50%',
                  bgcolor: 'rgba(255,255,255,0.22)',
                  backdropFilter: 'blur(10px)',
                  border: '1.5px solid rgba(255,255,255,0.45)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <ZoomInIcon sx={{ color: 'white', fontSize: { xs: '1.1rem', md: '1.25rem' } }} />
                </Box>
              </Box>

              {/* Image number */}
              <Box className="img-num" sx={{ position: 'absolute', bottom: { xs: 10, md: 14 }, left: { xs: 12, md: 16 }, zIndex: 3 }}>
                <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.68rem', fontWeight: 700, letterSpacing: 1.5 }}>
                  {String(index + 1).padStart(2, '0')}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Container>

      {/* ── LIGHTBOX ── */}
      <Dialog
        fullScreen
        open={selectedPhotoIndex !== null}
        onClose={closePhoto}
        TransitionComponent={Fade}
        PaperProps={{ sx: { bgcolor: 'rgba(15,23,42,0.96)', backgroundImage: 'none' } }}
      >
        {selectedPhotoIndex !== null && (
          <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', position: 'relative', userSelect: 'none' }}>

            {/* Top bar */}
            <Box sx={{
              position: 'absolute', top: 0, left: 0, right: 0, zIndex: 10,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              px: { xs: 2.5, md: 4 }, py: 2.5,
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.6) 0%, transparent 100%)',
            }}>
              <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.78rem', fontWeight: 600, letterSpacing: 2 }}>
                {String(selectedPhotoIndex + 1).padStart(2, '0')}
                <Box component="span" sx={{ mx: 0.75, color: 'rgba(255,255,255,0.2)' }}>/</Box>
                {String(photos.length).padStart(2, '0')}
              </Typography>
              <IconButton
                onClick={closePhoto}
                sx={{
                  color: 'white', width: 40, height: 40,
                  bgcolor: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  backdropFilter: 'blur(10px)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.18)' },
                }}
              >
                <CloseIcon sx={{ fontSize: '1.1rem' }} />
              </IconButton>
            </Box>

            {/* Main image */}
            <Box sx={{
              flex: 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              position: 'relative',
              px: { xs: 1, md: 10 },
            }}>
              <IconButton
                onClick={handlePrev}
                disabled={selectedPhotoIndex === 0}
                sx={{
                  position: 'absolute', left: { xs: 6, md: 28 }, zIndex: 5,
                  color: 'white', width: { xs: 40, md: 48 }, height: { xs: 40, md: 48 },
                  bgcolor: 'rgba(255,255,255,0.09)',
                  border: '1px solid rgba(255,255,255,0.14)',
                  backdropFilter: 'blur(10px)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.16)' },
                  '&.Mui-disabled': { opacity: 0.2 },
                }}
              >
                <WestIcon sx={{ fontSize: '1rem' }} />
              </IconButton>

              <Fade in key={selectedPhotoIndex} timeout={180}>
                <Box sx={{
                  borderRadius: { xs: '12px', md: '16px' },
                  overflow: 'hidden',
                  boxShadow: '0 32px 80px rgba(0,0,0,0.7)',
                  display: 'flex',
                }}>
                  <img
                    src={photos[selectedPhotoIndex]}
                    alt={`Gallery ${selectedPhotoIndex + 1}`}
                    style={{
                      maxWidth: '100%',
                      maxHeight: 'calc(100vh - 200px)',
                      objectFit: 'contain',
                      display: 'block',
                    }}
                  />
                </Box>
              </Fade>

              <IconButton
                onClick={handleNext}
                disabled={selectedPhotoIndex === photos.length - 1}
                sx={{
                  position: 'absolute', right: { xs: 6, md: 28 }, zIndex: 5,
                  color: 'white', width: { xs: 40, md: 48 }, height: { xs: 40, md: 48 },
                  bgcolor: 'rgba(255,255,255,0.09)',
                  border: '1px solid rgba(255,255,255,0.14)',
                  backdropFilter: 'blur(10px)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.16)' },
                  '&.Mui-disabled': { opacity: 0.2 },
                }}
              >
                <EastIcon sx={{ fontSize: '1rem' }} />
              </IconButton>
            </Box>

            {/* Thumbnail filmstrip */}
            <Box sx={{
              py: 2.5, px: 2,
              background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 100%)',
              display: 'flex', justifyContent: 'center', gap: 1,
              overflowX: 'auto',
              '&::-webkit-scrollbar': { display: 'none' },
              scrollbarWidth: 'none',
            }}>
              {photos.map((thumb, i) => (
                <Box
                  key={i}
                  onClick={() => setSelectedPhotoIndex(i)}
                  sx={{
                    width: { xs: 44, md: 56 }, height: { xs: 44, md: 56 },
                    flexShrink: 0, borderRadius: '8px', overflow: 'hidden',
                    cursor: 'pointer',
                    opacity: i === selectedPhotoIndex ? 1 : 0.3,
                    outline: i === selectedPhotoIndex ? '2px solid #f97316' : '2px solid transparent',
                    outlineOffset: '2px',
                    transform: i === selectedPhotoIndex ? 'scale(1.1)' : 'scale(1)',
                    transition: 'all 0.2s ease',
                    '&:hover': { opacity: 0.75 },
                  }}
                >
                  <img src={thumb} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Dialog>
    </Box>
  );
};

export default Gallery;
